import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, input, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { ToastService } from '../../../@core/services/toast.service';
import { DialogService } from '../../../@core/services/dialog.service';
import { DocuSignService } from '../../services/docu-sign.service';
import { IntegrationsService } from '../../services/integrations.service';
import { SIGNATORY_TYPE } from '../../models/contract-management.model';
import { INTEGRATION_TYPE } from '../../models/integration-types.enum';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

@Component({
  selector: 'app-docu-sign',
  standalone: true,
  imports: [CommonModule, ButtonComponent, InputTextModule, ReactiveFormsModule, PasswordModule, AssetUrlPipe],
  templateUrl: './docu-sign.component.html',
  styleUrl: './docu-sign.component.scss'
})
export class DocuSignComponent {

  @Output() close = new EventEmitter<{ connected?: boolean }>();

  docuForm!: FormGroup;
  consentExists: boolean = false;
  connectLoading: boolean = false;
  revokeLoading: boolean = false;
  dots = [].constructor(5);
  lastSynced: string = '';

  constructor(
    private fb: FormBuilder,
    private docuSignService: DocuSignService,
    private activatedRoute: ActivatedRoute,
    private toast: ToastService,
    private router: Router,
    private location: Location,
    private dialogService: DialogService,
    private integrationsService: IntegrationsService
  ) { }

  ngOnInit() {
    const code = this.activatedRoute.snapshot.queryParamMap.get('code')
    if (code) {
      this.setDocusignOAuthToken(code)
    }

    this.docuForm = this.fb.group({
      clientId: [''],
      clientSecret: [''],
      apiAccountId: ['']
    });
    this.getDocuSignSettings();
    this.checkConsent();
  }

  getDocuSignSettings() {
    this.docuSignService.getDocuSignSettings().subscribe((res) => {
      if (res.success) {
        this.docuForm.get('clientId')?.setValue(res.data?.clientId);
        this.docuForm.get('clientSecret')?.setValue(res.data?.clientSecret);
        this.docuForm.get('apiAccountId')?.setValue(res.data?.apiAccountId);
        this.lastSynced = res.data ? moment(res.data?.updatedAt).format('lll') : ""
      }
    })
  }

  setDocusignOAuthToken(code: string) {
    this.docuSignService.setAccessToken(code).subscribe((response) => {
      if (response && response.success) {
        this.toast.success('Docusign Consent Granted!')
        this.consentExists = true
        this.close.emit({ connected: true });
        this.location.replaceState('/settings/integrations');
      }
    })
  }

  saveDocuSignSettings(showToast = true) {
    return new Promise((resolve, reject) => {
      const credentials = this.docuForm.value;
      this.docuSignService.saveDocuSignSettings(credentials).subscribe((res) => {
        if (res.success) {
          if (showToast) this.toast.success('Docusign settings saved successfully!');
          resolve(true)
        } else {
          resolve(false)
        }
      })
    })
  }

  checkConsent() {
    this.docuSignService.authDocuSign().subscribe((response) => {
      const result = response.data
      if (result.exists) {
        this.consentExists = true
      }
    })
  }

  async checkIfAnotherSignatoryConnected(): Promise<{ isConnected: boolean; signatoryName: string }> {
    return new Promise((resolve) => {
      this.integrationsService.getAllIntegrations().subscribe((res: any) => {
        if (res.success) {
          const integrations = Array.isArray(res.data)
            ? res.data
            : (res.data?.integrations || res.data || []);

          const signatoryIntegration = integrations.find(
            (integration: any) => integration.type === INTEGRATION_TYPE.SIGNATORY_SETTINGS
          );

          if (signatoryIntegration && signatoryIntegration.values && Array.isArray(signatoryIntegration.values) && signatoryIntegration.values.length > 0) {
            const connectedValues = signatoryIntegration.values.map((v: any) => String(v));
            // Check if Adobe Sign is connected (not DocuSign)
            if (connectedValues.includes(String(SIGNATORY_TYPE.ADOBE_SIGN))) {
              resolve({ isConnected: true, signatoryName: 'Adobe Sign' });
              return;
            }
          }
        }
        resolve({ isConnected: false, signatoryName: '' });
      });
    });
  }

  async getConsentCode() {
    // Check if another signatory is already connected
    const { isConnected, signatoryName } = await this.checkIfAnotherSignatoryConnected();

    if (isConnected) {
      this.dialogService
        .confirm({
          title: 'Confirm Signatory Change',
          description: `Previous signatory (${signatoryName}) consent will be revoked. Do you want to proceed?`,
          confirmText: 'Proceed',
          cancelText: 'Cancel',
        })
        .subscribe((confirmed) => {
          if (confirmed) this.proceedWithConsent();
        });
    } else {
      // No other signatory connected, proceed directly
      this.proceedWithConsent();
    }
  }

  private async proceedWithConsent() {
    this.connectLoading = true;
    const saveDocuSettings = await this.saveDocuSignSettings(false)
    if (!saveDocuSettings) {
      this.toast.error('Docusign settings not saved successfully!')
      this.connectLoading = false;
      return;
    }
    this.docuSignService.authDocuSign().subscribe({
      next: (response) => {
        const result = response.data
        if (result.exists) {
          this.consentExists = true
          this.toast.success('Docusign Consent Granted!')
          this.close.emit({ connected: true });
        } else {
          const _payload = result.payload
          const url = new URL(_payload.url)
          url.searchParams.append('response_type', _payload.response_type)
          url.searchParams.append('scope', _payload.scope)
          url.searchParams.append('redirect_uri', location.href + '/docu-sign')
          url.searchParams.append('client_id', _payload.client_id)
          location.href = url.toString()
        }
        this.connectLoading = false;
      },
      error: () => {
        this.connectLoading = false;
      }
    })
  }

  revokeConsent() {
    this.docuSignService.revokeConsent().subscribe((response) => {
      if (response) {
        this.consentExists = false
        this.toast.success('Docusign Consent Revoked!')
        this.getDocuSignSettings()
        this.revokeLoading = false
        this.close.emit({ connected: false });
      }
    })
  }
}
